import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, Input } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  QUEUE_LABEL,
  QUEUE_ORDER,
  STATE_LABEL,
  payoutReconciliationService as api,
  type DebitRow,
  type PayoutRow,
  type QueueName,
} from '@/services/payoutReconciliation';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatCurrency, formatDate } from '@/utils';

/**
 * #690: Reconciliation > POSP payouts. Queues (completed but not in bank, bank debits with no payout,
 * part paid, cash without receipt, pending, bulk, confirmed) and each POSP's position. Suggestions are
 * only suggestions: a person presses Confirm. Unmatching asks for a reason; nothing is deleted.
 */

const STATE_VARIANT = {
  pending: 'default',
  not_in_bank: 'warning',
  cash_no_receipt: 'warning',
  part_confirmed: 'info',
  confirmed: 'success',
  void: 'default',
} as const;

export function PayoutReconciliationTab() {
  const canEdit = useCan('reconciliation', 'edit');
  const qc = useQueryClient();
  const [queue, setQueue] = useState<QueueName>('not_in_bank');
  const [openLine, setOpenLine] = useState<string | null>(null); // debit line showing suggestions
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const [receipt, setReceipt] = useState({ amount: '', receipt_date: '', reference: '' });
  const [unmatchFor, setUnmatchFor] = useState<{ kind: 'line' | 'receipt'; id: string } | null>(null);
  const [reason, setReason] = useState('');

  const counts = useQuery({ queryKey: ['payoutRecon', 'counts'], queryFn: () => api.counts() });
  const rows = useQuery({ queryKey: ['payoutRecon', 'queue', queue], queryFn: () => api.queue(queue) });
  const positions = useQuery({ queryKey: ['payoutRecon', 'positions'], queryFn: () => api.positions() });
  const suggestions = useQuery({
    queryKey: ['payoutRecon', 'suggestions', openLine],
    queryFn: () => api.suggestions(openLine as string),
    enabled: !!openLine,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['payoutRecon'] });
  const onError = (e: unknown) => toast.error(getErrorMessage(e));

  const confirm = useMutation({
    mutationFn: (v: { line: string; allocations: { payout: string; amount: string }[] }) => api.confirm(v.line, v.allocations),
    onSuccess: () => {
      toast.success('Confirmed in bank');
      setOpenLine(null);
      refresh();
    },
    onError,
  });
  const record = useMutation({
    mutationFn: (payout: string) => api.receipt({ payout, ...receipt }),
    onSuccess: () => {
      toast.success('Receipt recorded');
      setReceiptFor(null);
      setReceipt({ amount: '', receipt_date: '', reference: '' });
      refresh();
    },
    onError,
  });
  const unmatch = useMutation({
    mutationFn: () => (unmatchFor!.kind === 'line' ? api.unmatch(unmatchFor!.id, reason) : api.removeReceipt(unmatchFor!.id, reason)),
    onSuccess: () => {
      toast.success('Unmatched');
      setUnmatchFor(null);
      setReason('');
      refresh();
    },
    onError,
  });

  const isDebitQueue = queue === 'unknown_debits' || queue === 'bulk';
  const results = rows.data?.results ?? [];

  const reasonForm = (
    <div className="mt-2 flex items-end gap-2">
      <Input label="Reason" name="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Button size="sm" onClick={() => unmatch.mutate()} disabled={!reason.trim()} isLoading={unmatch.isPending}>
        Unmatch
      </Button>
      <Button size="sm" variant="outline" onClick={() => setUnmatchFor(null)}>
        Cancel
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="POSP payouts"
        subtitle="Match bank debits to the payouts they settled, and see what each POSP is still owed"
        breadcrumbs={[{ label: 'Reconciliation' }, { label: 'POSP payouts' }]}
      />

      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Queues">
        {QUEUE_ORDER.map((q) => (
          <button
            key={q}
            type="button"
            aria-pressed={queue === q}
            onClick={() => {
              setQueue(q);
              setOpenLine(null);
            }}
            className={`rounded-md border px-3 py-1.5 text-sm ${
              queue === q
                ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300'
            }`}
          >
            {QUEUE_LABEL[q]} <span className="font-semibold">{counts.data?.[q] ?? 0}</span>
          </button>
        ))}
      </div>

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{QUEUE_LABEL[queue]}</h3>
        {rows.isError ? (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            Could not load: {getErrorMessage(rows.error)}
          </p>
        ) : results.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Nothing in this queue.</p>
        ) : isDebitQueue ? (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Narration</th>
                  <th className="py-2 pr-4">Reference</th>
                  <th className="py-2 pr-4 text-right">Debit</th>
                  <th className="py-2">{queue === 'bulk' ? 'Paid' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {(results as DebitRow[]).map((l) => (
                  <tr key={l.id} className="border-t border-gray-100 align-top dark:border-gray-800">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(l.txn_date)}</td>
                    <td className="py-2 pr-4">{l.narration}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{l.reference}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(l.debit)}</td>
                    <td className="py-2">
                      {queue === 'bulk' ? (
                        <>
                          <ul className="text-xs">
                            {(l.payouts ?? []).map((m) => (
                              <li key={m.id}>
                                {m.payout_reference}: {formatCurrency(m.amount)}
                              </li>
                            ))}
                          </ul>
                          {canEdit && (
                            <Button size="sm" variant="outline" onClick={() => setUnmatchFor({ kind: 'line', id: l.id })}>
                              Unmatch
                            </Button>
                          )}
                          {unmatchFor?.id === l.id && reasonForm}
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setOpenLine(openLine === l.id ? null : l.id)}>
                            Find payout
                          </Button>
                          {openLine === l.id && (
                            <div className="mt-2 space-y-2" aria-label="Suggestions">
                              {suggestions.isLoading && <p className="text-xs text-gray-500">Looking...</p>}
                              {suggestions.data?.length === 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">No payout fits this debit.</p>
                              )}
                              {suggestions.data?.map((s, i) => (
                                <div key={i} className="rounded border border-gray-200 p-2 text-xs dark:border-gray-700">
                                  <p className="font-medium">
                                    {s.kind === 'bulk' ? 'Batch' : 'Payout'}: {formatCurrency(s.total)} ({s.reasons.join(', ')})
                                  </p>
                                  <ul>
                                    {s.payouts.map((p) => (
                                      <li key={p.payout}>
                                        {p.payout_reference} - {p.posp_name} ({p.posp_code}): {formatCurrency(p.amount)}
                                      </li>
                                    ))}
                                  </ul>
                                  {canEdit && (
                                    <Button
                                      size="sm"
                                      className="mt-1"
                                      isLoading={confirm.isPending}
                                      onClick={() =>
                                        confirm.mutate({
                                          line: l.id,
                                          allocations: s.payouts.map((p) => ({ payout: p.payout, amount: p.amount })),
                                        })
                                      }
                                    >
                                      Confirm
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Payout</th>
                  <th className="py-2 pr-4">POSP</th>
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4">Mode</th>
                  <th className="py-2 pr-4 text-right">Net</th>
                  <th className="py-2 pr-4 text-right">Confirmed</th>
                  <th className="py-2 pr-4">Bank reference</th>
                  <th className="py-2">State</th>
                </tr>
              </thead>
              <tbody>
                {(results as PayoutRow[]).map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 align-top dark:border-gray-800">
                    <td className="py-2 pr-4 font-mono text-xs">{p.payout_reference}</td>
                    <td className="py-2 pr-4">
                      {p.posp_name} <span className="text-xs text-gray-500">{p.posp_code}</span>
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {formatDate(p.period_start)} - {formatDate(p.period_end)}
                    </td>
                    <td className="py-2 pr-4">{p.payment_mode.replace('_', ' ')}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.net_amount)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.confirmed)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{p.bank_references.join(', ')}</td>
                    <td className="py-2">
                      <Badge variant={STATE_VARIANT[p.state]} size="sm">
                        {STATE_LABEL[p.state]}
                      </Badge>
                      {canEdit && p.payment_mode === 'cash' && p.state !== 'confirmed' && p.state !== 'pending' && (
                        <div className="mt-2">
                          <Button size="sm" variant="outline" onClick={() => setReceiptFor(receiptFor === p.id ? null : p.id)}>
                            Record receipt
                          </Button>
                          {receiptFor === p.id && (
                            <div className="mt-2 grid gap-2 md:grid-cols-4">
                              <Input
                                label="Amount"
                                name="amount"
                                inputMode="decimal"
                                value={receipt.amount}
                                onChange={(e) => setReceipt({ ...receipt, amount: e.target.value })}
                              />
                              <Input
                                label="Receipt date"
                                name="receipt_date"
                                type="date"
                                value={receipt.receipt_date}
                                onChange={(e) => setReceipt({ ...receipt, receipt_date: e.target.value })}
                              />
                              <Input
                                label="Receipt number"
                                name="reference"
                                value={receipt.reference}
                                onChange={(e) => setReceipt({ ...receipt, reference: e.target.value })}
                              />
                              <Button
                                size="sm"
                                className="self-end"
                                disabled={!receipt.amount || !receipt.receipt_date || !receipt.reference.trim()}
                                isLoading={record.isPending}
                                onClick={() => record.mutate(p.id)}
                              >
                                Save receipt
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      {canEdit &&
                        p.matches.map((m) => (
                          <div key={m.id} className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            {m.kind === 'bank' ? 'Bank' : 'Receipt'} {m.reference} {formatCurrency(m.amount)}{' '}
                            <button
                              type="button"
                              className="underline"
                              onClick={() =>
                                setUnmatchFor(m.kind === 'bank' ? { kind: 'line', id: m.bank_line as string } : { kind: 'receipt', id: m.id })
                              }
                            >
                              Unmatch
                            </button>
                            {unmatchFor && unmatchFor.id === (m.kind === 'bank' ? m.bank_line : m.id) && reasonForm}
                          </div>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Position per POSP</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Payable = earned - clawbacks - TDS - paid in bank. Difference is what the ledger calls paid but the bank (or a receipt) has not proved.
        </p>
        {positions.isError ? (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            Could not load positions: {getErrorMessage(positions.error)}
          </p>
        ) : (positions.data ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No POSP has ledger entries yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">POSP</th>
                  <th className="py-2 pr-4 text-right">Earned</th>
                  <th className="py-2 pr-4 text-right">Clawbacks</th>
                  <th className="py-2 pr-4 text-right">TDS</th>
                  <th className="py-2 pr-4 text-right">Paid in bank</th>
                  <th className="py-2 pr-4 text-right">Payable</th>
                  <th className="py-2 pr-4 text-right">Difference</th>
                  <th className="py-2 text-right">Unconfirmed</th>
                </tr>
              </thead>
              <tbody>
                {positions.data?.map((p) => (
                  <tr key={p.posp} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      {p.posp_name} <span className="text-xs text-gray-500">{p.posp_code}</span>
                    </td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.earned)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.clawback)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.tds)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.paid_in_bank)}</td>
                    <td className="py-2 pr-4 text-right font-medium">{formatCurrency(p.payable)}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(p.difference)}</td>
                    <td className="py-2 text-right">{p.unconfirmed_payouts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
